import BadgeIcon from './BadgeIcon';
import React, { useState, useRef } from 'react';
import { AnimatePresence, motion, useSpring, transform } from 'motion/react';
import { useLayer, useHover, Arrow } from 'react-laag';
import { FaGift } from 'react-icons/fa6';
import './NeoBadge.css';
import './SnappyInfo.css';

function SnappyInfo({ onInteractionChange, snappyDetails }) {
	const [isOpen, setOpen] = React.useState(false);
	const [isHovering, setIsHovering] = React.useState(false);

	const { renderLayer, triggerProps, layerProps } = useLayer({
		isOpen,
		placement: 'bottom-center',
		auto: true,
		triggerOffset: 8, // Distance between trigger and layer
		containerOffset: 16, // Minimum distance to container edges
		arrowOffset: 8, // Distance between layer and arrow
	});

	React.useEffect(() => {
		if (onInteractionChange) {
			onInteractionChange(isOpen || isHovering);
		}
	}, [isOpen, isHovering, onInteractionChange]);

	const handleMouseEvents = (e) => {
		e.stopPropagation();
	};
	if (layerProps?.style) {
		layerProps.style.zIndex = 9999;
	}

	return (
		<>
			<button
				className="snappy-button"
				{...triggerProps}
				onClick={(e) => {
					e.stopPropagation();
					setOpen(!isOpen);
				}}
				onMouseEnter={(e) => {
					handleMouseEvents(e);
					setIsHovering(true);
				}}
				onMouseMove={handleMouseEvents}
				onMouseLeave={(e) => {
					handleMouseEvents(e);
					setIsHovering(false);
				}}
			>
				<FaGift color="#ffffff" size={20} aria-label="Snappy Rewards" />
			</button>
			{renderLayer(
				<AnimatePresence>
					{isOpen && (
						<motion.div
							{...layerProps}
							initial={{ opacity: 0, y: -5 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -5 }}
							transition={{ duration: 0.2 }}
							className="snappy-popup"
							// style={{zIndex: 9999}}
							onClick={handleMouseEvents}
							onMouseEnter={(e) => {
								handleMouseEvents(e);
								setIsHovering(true);
							}}
							onMouseMove={handleMouseEvents}
							onMouseLeave={(e) => {
								handleMouseEvents(e);
								setIsHovering(false);
							}}
						>
							<h4 className="snappy-popup-header">
								{snappyDetails?.snappyGrantName || 'Snappy Reward'}
							</h4>
							<p className="snappy-popup-amount">
								{snappyDetails?.costInCurrency || '$0'}
							</p>
							<p className="snappy-popup-description">
								{snappyDetails?.description || 'Snappy Gift Attached ⭐️'}
							</p>
						</motion.div>
					)}
				</AnimatePresence>
			)}
		</>
	);
}

const NeoBadge = ({
	hideSnappyIcon = true,
	snappyDetails = {},
	shape = 'hexagon',
	badgeRotation = 0,
	icon = 'ANCHOR',
	colors = {
		gradient: 'linear-gradient(to bottom right, #6366f1, #0ea5e9, #10b981)',
		icon: '#ffffff',
		text: '#ffffff',
	},
	text,
	captureId,
	useTilt = true,
	customImage = null,
	labelColor = null,
	customIconSignedUrl = null,
}) => {
	const [isHovered, setIsHovered] = useState(false);
	const [snappyInteracting, setSnappyInteracting] = useState(false);
	const [frame, setFrame] = useState({
		width: 0,
		height: 0,
		top: 0,
		left: 0,
	});
	const badgeRef = useRef(null);

	const rotateValue = 5;
	const rotateX3Speed = rotateValue * 1.9;
	const higherSpeed = rotateValue * 2; // highest
	const baseSpeed = rotateValue * 1.1;
	const slowerSpeed = rotateValue * 0.75;
	const slowestSpeed = rotateValue * 0.5; // lowest
	const springValue = { stiffness: 400, damping: 30 };

	const rotateX = useSpring(0, springValue);
	const rotateY = useSpring(0, springValue);
	const rotateX2 = useSpring(0, springValue);
	const rotateY2 = useSpring(0, springValue);
	const rotateX3 = useSpring(0, springValue);
	const rotateY3 = useSpring(0, springValue);
	const rotateX4 = useSpring(0, springValue);
	const rotateY4 = useSpring(0, springValue);
	const x = useSpring(0, springValue);
	const y = useSpring(0, springValue);
	const x2 = useSpring(0, springValue);
	const y2 = useSpring(0, springValue);
	const x3 = useSpring(0, springValue);
	const y3 = useSpring(0, springValue);
	const x4 = useSpring(0, springValue);
	const y4 = useSpring(0, springValue);
	const shadowX = useSpring(0, springValue);
	const shadowY = useSpring(30, springValue);
	const shadow2X = useSpring(0, springValue);
	const shadow2Y = useSpring(10, springValue);

	const convertCursorPosition = (e) => {
		const objectX = (e.nativeEvent.clientX - frame.left) / frame.width;
		const objectY = (e.nativeEvent.clientY - frame.top) / frame.height;

		const rotateX4Val = transform(objectY, [0, 1], [baseSpeed, -baseSpeed]);
		const rotateY4Val = transform(
			objectX,
			[0, 1],
			[-slowestSpeed, slowestSpeed]
		);
		const rotateX3Val = transform(
			objectY,
			[0, 1],
			[rotateX3Speed, -rotateX3Speed]
		);
		const rotateY3Val = transform(
			objectX,
			[0, 1],
			[-rotateX3Speed, rotateX3Speed]
		);

		rotateX4.set(rotateX4Val);
		rotateY4.set(rotateY4Val);
		rotateX3.set(rotateX3Val);
		rotateY3.set(rotateY3Val);
		rotateX2.set(transform(objectY, [0, 1], [slowerSpeed, -slowerSpeed]));
		rotateY2.set(transform(objectX, [0, 1], [-slowerSpeed, slowerSpeed]));
		rotateX.set(transform(objectY, [0, 1], [-slowestSpeed, -slowestSpeed]));
		rotateY.set(transform(objectX, [0, 1], [-slowestSpeed, -slowestSpeed]));
		x.set(transform(objectX, [0, 1], [-higherSpeed, higherSpeed]));
		y.set(transform(objectY, [0, 1], [-higherSpeed, higherSpeed]));
		x2.set(transform(objectX, [0, 1], [-rotateValue, rotateValue]));
		y2.set(transform(objectY, [0, 1], [-rotateValue, rotateValue]));
		x3.set(transform(objectX, [0, 1], [-slowerSpeed, slowerSpeed]));
		y3.set(transform(objectY, [0, 1], [-slowerSpeed, slowerSpeed]));
		x4.set(transform(objectX, [0, 1], [-slowestSpeed, slowestSpeed]));
		y4.set(transform(objectY, [0, 1], [-slowestSpeed, slowestSpeed]));

		shadowX.set(transform(objectX, [0, 1], [20, -20]));
		shadowY.set(transform(objectY, [0, 1], [60, 20]));
		shadow2X.set(transform(objectX, [0, 1], [20, -20]));
		shadow2Y.set(transform(objectY, [0, 1], [40, 20]));
	};

	/* On Mouse Enter, get object frame and convert values */
	const handleMouseEnter = (e) => {
		if (!useTilt) return;
		// Skip if Snappy component is being interacted with
		setIsHovered(true);
		if (snappyInteracting) return;

		const currentElement = e.target.getBoundingClientRect();
		if (e.target._meaningfulType == 'IMAGE') return;

		setFrame({
			width: currentElement.width,
			height: currentElement.height,
			top: currentElement.top,
			left: currentElement.left,
		});

		convertCursorPosition(e);
	};

	/* On Mouse Move, convert values */
	const handleMouseMove = (e) => {
		if (!useTilt) return;
		// Skip if Snappy component is being interacted with
		if (snappyInteracting) return;

		if (e.target._meaningfulType == 'IMAGE') return;
		convertCursorPosition(e);
	};

	/* On Mouse Leave, reset */
	const handleMouseLeave = (e) => {
		if (!useTilt) return;
		setIsHovered(false);
		rotateX.set(0);
		rotateY.set(0);
		rotateX2.set(0);
		rotateY2.set(0);
		rotateX3.set(0);
		rotateY3.set(0);
		rotateX4.set(0);
		rotateY4.set(0);
		x.set(0);
		y.set(0);
		x2.set(0);
		y2.set(0);
		x3.set(0);
		y3.set(0);
		x4.set(0);
		y4.set(0);
		shadowX.set(0);
		shadowY.set(40);
		shadow2X.set(0);
		shadow2Y.set(20);
	};

	// React-laag popover for Snappy icon
	const [hoverProps, isSnappyHovered] = useHover();

	const getBadgeShapeClass = () => {
		switch (shape) {
			case 'circle':
				return 'neo-badge-circle-shape';
			case 'hexagon':
				return 'neo-badge-hexagon-shape';
			case 'flower':
				return 'neo-badge-flower-shape';
			case 'wavy':
				return 'neo-badge-wavy-shape';
			case 'starburst':
				return 'neo-badge-starburst-shape';
			case 'shield':
				return 'neo-badge-shield-shape';
			default:
				return 'neo-badge-hexagon-shape';
		}
	};

	return (
		<>
			<motion.div
				id={captureId?.toString() ? captureId?.toString() : null}
				ref={badgeRef}
				className="neo-badge-container"
				onMouseMove={handleMouseMove}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				initial={false}
				style={{
					rotateX: useTilt ? rotateX4 : 0,
					rotateY: useTilt ? rotateY4 : 0,
					zIndex: 201,
					transformStyle: 'preserve-3d',
					perspective: '10000px',
					transformOrigin: 'center center',
				}}
				transition={
					useTilt
						? {
								scale: { type: 'spring', stiffness: 300, damping: 40 },
							}
						: { duration: 0 }
				}
			>
				{customImage ? (
					<>
						{/* Shadow layer - sits behind the image */}
						<motion.img
							src={customImage}
							alt=""
							aria-hidden="true"
							initial={false}
							style={{
								gridArea: '1/-1',
								width: 260,
								height: 260,
								objectFit: 'contain',
								zIndex: 200,
								opacity: 0.3,
								filter: 'blur(12px) brightness(0)',
								transformStyle: 'preserve-3d',
								transformOrigin: 'center center',
								x: useTilt ? shadowX : 0,
								y: useTilt ? shadowY : 40,
								scale: 0.85,
								translateY: 10,
								pointerEvents: 'none',
							}}
						/>
						{/* Image layer */}
						<motion.img
							src={customImage}
							alt="Custom badge"
							initial={false}
							style={{
								gridArea: '1/-1',
								width: 260,
								height: 260,
								objectFit: 'contain',
								backfaceVisibility: 'hidden',
								WebkitBackfaceVisibility: 'hidden',
								zIndex: 201,
								rotateX: useTilt ? rotateX3 : 0,
								rotateY: useTilt ? rotateY3 : 0,
								x: useTilt ? x2 : 0,
								y: useTilt ? y2 : 0,
								translateZ: 30,
								transformStyle: 'preserve-3d',
								perspective: '800px',
								transformOrigin: 'center center',
								pointerEvents: 'auto',
							}}
							transition={
								useTilt
									? { type: 'spring', stiffness: 400, damping: 20 }
									: { duration: 0 }
							}
						/>
					</>
				) : (
					<>
						{/* Layer 1: Badge Shape - Base Layer */}
						<motion.div
							className={`neo-badge-shape ${getBadgeShapeClass()}`}
							initial={false}
							style={{
								background: colors.gradient,
								backfaceVisibility: 'hidden',
								WebkitBackfaceVisibility: 'hidden',
								zIndex: 201,
								width: 260,
								height: 260,
								rotate: badgeRotation || 0,
								rotateX: useTilt ? rotateX4 : 0,
								rotateY: useTilt ? rotateY4 : 0,
								transformStyle: 'preserve-3d',
								perspective: '10000px',
								placeItems: 'center',
								placeContent: 'center',
								transformOrigin: 'center center',
							}}
						/>
						{/* Layer 2: White Circle - Separate from badge shape */}
						<motion.div
							className="neo-badge-circle"
							initial={false}
							style={{
								backfaceVisibility: 'hidden',
								WebkitBackfaceVisibility: 'hidden',
								zIndex: 250,
								position: 'relative',
								rotateX: useTilt ? rotateX4 : 0,
								rotateY: useTilt ? rotateY4 : 0,
								transformStyle: 'preserve-3d',
								perspective: '10000px',
								transformOrigin: 'center center',
								translateZ: 20,
								x: useTilt ? x3 : 0,
								y: useTilt ? y3 : 0,
							}}
							transition={
								useTilt
									? { type: 'spring', stiffness: 400, damping: 30 }
									: { duration: 0 }
							}
						/>
						<motion.div
							initial={false}
							style={{
								backfaceVisibility: 'hidden',
								WebkitBackfaceVisibility: 'hidden',
								zIndex: 500,
								alignSelf: 'center',
								position: 'relative',
								gridArea: '1/-1',
								justifySelf: 'center',
								rotateX: useTilt ? rotateX4 : 0,
								rotateY: useTilt ? rotateY4 : 0,
								transformStyle: 'preserve-3d',
								perspective: '10000px',
								transformOrigin: 'center center',
								translateZ: 30,
								x: useTilt ? x2 : 0,
								y: useTilt ? y2 : 0,
							}}
							transition={
								useTilt
									? { type: 'spring', stiffness: 400, damping: 20 }
									: { duration: 0 }
							}
						>
							{customIconSignedUrl ? (
								<div
									aria-label="Custom icon"
									style={{
										width: 64,
										height: 64,
										backgroundColor: colors.icon,
										WebkitMaskImage: `url(${customIconSignedUrl})`,
										maskImage: `url(${customIconSignedUrl})`,
										WebkitMaskSize: 'contain',
										maskSize: 'contain',
										WebkitMaskRepeat: 'no-repeat',
										maskRepeat: 'no-repeat',
										WebkitMaskPosition: 'center',
										maskPosition: 'center',
										zIndex: 10000,
									}}
								/>
							) : (
								<BadgeIcon
									style={{
										zIndex: 10000,
										rotateX: useTilt ? rotateX4 : 0,
										rotateY: useTilt ? rotateY4 : 0,
										transformStyle: 'preserve-3d',
										perspective: '10000px',
										transformOrigin: 'center center',
									}}
									iconName={icon}
									size={64}
									className="neo-badge-icon"
									color={colors.icon}
									disableAnimation={!useTilt}
								/>
							)}
						</motion.div>
						{/* Layer 4: Label - Separate from badge shape */}
						<motion.div
							className="neo-badge-label"
							initial={false}
							style={{
								backfaceVisibility: 'hidden',
								WebkitBackfaceVisibility: 'hidden',
								alignSelf: 'self-end',
								zIndex: 10000,
								position: 'relative',
								rotateX: useTilt ? rotateX4 : 0,
								rotateY: useTilt ? rotateY4 : 0,
								transformStyle: 'preserve-3d',
								perspective: '10000px',
								transformOrigin: 'center center',
								x: useTilt ? x : 0,
								y: useTilt ? y : 0,
								translateZ: 50,
								overflow: 'visible',
								...(labelColor ? { backgroundImage: 'none', backgroundColor: labelColor } : {}),
							}}
							transition={
								useTilt
									? { type: 'spring', stiffness: 400, damping: 20 }
									: { duration: 0 }
							}
						>
							<span
								className="neo-badge-text"
								style={{
									zIndex: 10000,
									rotateX: useTilt ? rotateX4 : 0,
									rotateY: useTilt ? rotateY4 : 0,
									transformStyle: 'preserve-3d',
									perspective: '10000px',
									transformOrigin: 'center center',
									color: colors.text,
								}}
							>
								{text}
							</span>
						</motion.div>

						{/* Layer 5: Snappy Icon - Top right corner */}
						{(() => {
							return (
								!hideSnappyIcon &&
								snappyDetails?.snappyGrantId && (
									<motion.div
										className="neo-badge-snappy-icon"
										initial={false}
										style={{
											gridArea: '1/-1',
											alignSelf: 'self-start',
											justifySelf: 'end',
											textAlign: 'end',
											zIndex: 10,
											backfaceVisibility: 'hidden',
											WebkitBackfaceVisibility: 'hidden',
											pointerEvents: 'auto',
										}}
										animate={
											useTilt
												? {
														translateZ: 40,
													}
												: false
										}
										transition={
											useTilt
												? { type: 'spring', stiffness: 400, damping: 30 }
												: { duration: 0 }
										}
										onClick={(e) => e.stopPropagation()}
									>
										<SnappyInfo
											snappyDetails={snappyDetails}
											onInteractionChange={setSnappyInteracting}
										/>
									</motion.div>
								)
							);
						})()}
					</>
				)}
			</motion.div>
		</>
	);
};

export default NeoBadge;
